import React, { useState, useRef } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUpload, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import './DateUploadModal.css';

const DateUploadModal = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  title, 
  description, 
  acceptedTypes,
  isUploading = false 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [executionDate, setExecutionDate] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  const theme = useSelector((state) => state.theme.theme);
  const isDark = theme === 'dark';

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = () => {
    if (selectedFile && executionDate) {
      onUpload(selectedFile, executionDate);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setExecutionDate('');
    setDragActive(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isValidFile = (file) => {
    if (!acceptedTypes) return true;
    const fileExtension = file.name.split('.').pop().toLowerCase();
    return acceptedTypes.includes(`.${fileExtension}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={`date-upload-modal ${isDark ? 'dark-modal' : ''}`}
      size="lg"
    >
      <ModalBody className={`modal-body-custom ${isDark ? 'dark-body' : ''}`}>
        <div className="date-form-group">
          <label className={`form-label ${isDark ? 'dark-label' : ''}`}>
            <i className="fas fa-calendar label-icon"></i>
            Fecha de Ejecución *
          </label>
          <input
            type="date"
            className={`date-input ${isDark ? 'dark-input' : ''}`}
            value={executionDate}
            onChange={(e) => setExecutionDate(e.target.value)}
            required
          />
        </div>

        <div className="file-upload-section">
          <label className={`form-label ${isDark ? 'dark-label' : ''}`}>
            <i className="fas fa-file-upload label-icon"></i>
            Seleccionar Archivo *
          </label>
          
          <div
            className={`file-drop-zone ${isDark ? 'dark-drop-zone' : ''} ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="fas fa-cloud-upload-alt drop-zone-icon"></i>
            <p className={`drop-zone-text ${isDark ? 'dark-text' : ''}`}>
              Haz clic para seleccionar o arrastra el archivo aquí
            </p>
            <p className={`drop-zone-subtext ${isDark ? 'dark-subtext' : ''}`}>
              Tipos permitidos: {acceptedTypes}
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              className="file-input-hidden"
              accept={acceptedTypes}
              onChange={handleFileSelect}
            />
          </div>

          {selectedFile && (
            <div className={`selected-file-info ${isDark ? 'dark-file-info' : ''}`}>
              <i className="fas fa-file-alt file-icon"></i>
              <div className="file-details">
                <h6 className={isDark ? 'dark-file-name' : ''}>{selectedFile.name}</h6>
                <p className={`file-size ${isDark ? 'dark-file-size' : ''}`}>{formatFileSize(selectedFile.size)}</p>
                {!isValidFile(selectedFile) && (
                  <p className="file-error">Tipo de archivo no válido</p>
                )}
              </div>
              <button
                type="button"
                className="remove-file-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
        </div>
      </ModalBody>

      <div className={`modal-footer-custom ${isDark ? 'dark-footer' : ''}`}>
        <button
          type="button"
          className={`btn-cancel ${isDark ? 'dark-cancel' : ''}`}
          onClick={handleClose}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn-upload"
          onClick={handleSubmit}
          disabled={!executionDate || !selectedFile || !isValidFile(selectedFile) || isUploading}
        >
          {isUploading ? (
            <>
              <i className="fas fa-spinner upload-spinner"></i>
              Subiendo...
            </>
          ) : (
            <>
              <i className="fas fa-upload"></i>
              Subir Archivo
            </>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default DateUploadModal;